/**
 * Explicit reconciliation of an uncertain dispatch (batch 8 T12c1, #3033): the user states what they established with the supplier.
 *
 *  - "Sent": the supplier has the order. The order becomes ordered; the time recorded is the time of this decision, not the real send
 *    time. An optional supplier reference may be added.
 *  - "Not sent": the supplier does NOT have the order. Nothing is resent and nothing is ordered. Sending again is a new review and a
 *    new, explicit confirmation.
 *
 * After an unknown outcome the dialog closes and the state is refreshed; the unknown outcome stays visible until acknowledged and the
 * decision is never repeated on its own.
 */

import { useState } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { DialogHeader } from '@/components/common/DialogHeader';
import { useResolveDispatch } from '@/hooks/useProcurementDispatch';
import { describeDispatchError } from './dispatchErrors';
import type { DispatchResolveResult, DispatchSummary, ResolveDispatchBody } from './dispatchTypes';

const RESOLVE_REASON_MIN = 3;
const RESOLVE_REASON_MAX = 1000;
const SUPPLIER_REF_MAX = 200;

interface ResolveDispatchDialogProps {
  open: boolean;
  onClose: () => void;
  summary: DispatchSummary;
  onDone?: ((result: DispatchResolveResult) => void) | undefined;
}

export function ResolveDispatchDialog({
  open,
  onClose,
  summary,
  onDone,
}: ResolveDispatchDialogProps) {
  const { t } = useTranslation('procurementDispatch');
  const { t: tc } = useTranslation('common');
  const command = useResolveDispatch();
  const [decision, setDecision] = useState<'sent' | 'not_sent' | ''>('');
  const [reason, setReason] = useState('');
  const [supplierRef, setSupplierRef] = useState('');
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [blocked, setBlocked] = useState(false);

  const trimmed = reason.trim();
  const ref = supplierRef.trim();
  const reasonProblem =
    trimmed.length < RESOLVE_REASON_MIN
      ? 'short'
      : trimmed.length > RESOLVE_REASON_MAX
        ? 'long'
        : null;
  const refProblem = decision === 'sent' && ref.length > SUPPLIER_REF_MAX;
  const invalid = decision === '' || reasonProblem !== null || refProblem;

  const submit = async () => {
    setTouched(true);
    if (invalid) return;
    setError(null);
    setBlocked(false);
    const body: ResolveDispatchBody = {
      decision,
      reason: trimmed,
      // Only a "sent" decision carries a supplier reference, and only when the user typed one.
      ...(decision === 'sent' && ref !== '' ? { supplierOrderRef: ref } : {}),
    };
    const result = await command.run({
      purchaseOrderId: summary.purchaseOrderId,
      dispatchId: summary.id,
      body,
    });
    if (result.status === 'success') {
      onDone?.(result.result);
      onClose();
    } else if (result.status === 'unknown') {
      onClose();
    } else if (result.status === 'refused') {
      setError(result.error);
    } else if (result.status === 'blocked') {
      setBlocked(true);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={command.isPending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      mobileTitle={t('resolve.title')}
      aria-label={t('resolve.title')}
    >
      <DialogHeader title={t('resolve.title')} onClose={command.isPending ? undefined : onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2">{t('resolve.intro')}</Typography>
          <FormControl error={touched && decision === ''} disabled={command.isPending}>
            <FormLabel id="resolve-decision">{t('resolve.decision')}</FormLabel>
            <RadioGroup
              aria-labelledby="resolve-decision"
              value={decision}
              onChange={(event) => setDecision(event.target.value as 'sent' | 'not_sent')}
            >
              <FormControlLabel value="sent" control={<Radio />} label={t('resolve.sent')} />
              <FormHelperText sx={{ mt: -1, ml: 4 }}>{t('resolve.sentHint')}</FormHelperText>
              <FormControlLabel value="not_sent" control={<Radio />} label={t('resolve.notSent')} />
              <FormHelperText sx={{ mt: -1, ml: 4 }}>{t('resolve.notSentHint')}</FormHelperText>
            </RadioGroup>
            {touched && decision === '' && (
              <FormHelperText>{t('validation.decisionRequired')}</FormHelperText>
            )}
          </FormControl>

          <TextField
            label={t('resolve.reason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            onBlur={() => setTouched(true)}
            required
            multiline
            minRows={2}
            disabled={command.isPending}
            error={touched && reasonProblem !== null}
            helperText={
              touched && reasonProblem === 'short'
                ? t('validation.reasonTooShort', { min: RESOLVE_REASON_MIN })
                : touched && reasonProblem === 'long'
                  ? t('validation.reasonTooLong', { max: RESOLVE_REASON_MAX })
                  : t('resolve.reasonHint')
            }
            slotProps={{ htmlInput: { maxLength: RESOLVE_REASON_MAX * 2 } }}
          />

          {decision === 'sent' && (
            <TextField
              label={t('resolve.supplierRef')}
              value={supplierRef}
              onChange={(event) => setSupplierRef(event.target.value)}
              disabled={command.isPending}
              error={refProblem}
              helperText={
                refProblem
                  ? t('validation.refTooLong', { max: SUPPLIER_REF_MAX })
                  : t('resolve.supplierRefHint')
              }
            />
          )}

          {decision === 'not_sent' && <Alert severity="info">{t('resolve.notSentNext')}</Alert>}
          {blocked && (
            <Alert severity="warning" role="alert">
              {t('unknown.blockedRepeat')}
            </Alert>
          )}
          {error !== null && (
            <Alert severity="error" role="alert">
              {describeDispatchError(t, error)}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={command.isPending}>
          {tc('close')}
        </Button>
        <Button variant="contained" onClick={() => void submit()} disabled={command.isPending}>
          {command.isPending ? t('resolve.saving') : t('resolve.confirm')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
